import { Controller } from "@hotwired/stimulus";
import * as mapboxgl from 'mapbox-gl';
import { Map, MapboxEvent, Marker, LngLat, LngLatBounds, Popup } from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import * as MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import * as StaticMode from '@mapbox/mapbox-gl-draw-static-mode';
import { FeatureCollection } from 'geojson';

import { createMap, getDrawStyles, buildMapFeatureForTriggerZone, getTourColor, showTriggerZonePopup, getOsmId } from '../../scripts/mapbox';
import { createOverlay, Overlay } from '../../scripts/loaderOverlay';
import { PointOfInterestMapMarkerModel } from '../../scripts/_generated/PointOfInterestMapMarkerModel';
import { TourMapMarkerModel } from '../../scripts/_generated/TourMapMarkerModel';
import { addBoundingBox, createPoiCreationMarker, forceMapToRedrawWhenInitiallyHidden, getPoiMarkerPopupHtml } from '../Map/MapComponentShared';
import "./DestinationMap.scss";
import { Colors } from "../../scripts/_generated/Colors";
import { IBoundingBox } from "../../scripts/_generated/IBoundingBox";
import { DomEvents } from "../../scripts/_generated/DomEvents";

export default class DestinationMapController extends Controller {
    static targets = [
        'container',
        'saveButton',
        'cancelButton'
    ];
    static values = {
        destinationUniqueId: String,
        destinationLanguage: String,
        boundingBox: Object,
        pois: Array,
        tours: Array,
        readonly: Boolean,
        getJobUrl: String,
        poiCreateUrl: String,
        poiViewUrl: String,
        poiEditUrl: String,
        poiDetailUrl: String,
        isNewPoiDetailPageEnabled: Boolean,
        tourDetailUrl: String,
        createPoiFromMapUrl: String
    }

    readonly containerTarget!: HTMLElement;
    readonly saveButtonTarget!: HTMLButtonElement;
    readonly cancelButtonTarget!: HTMLButtonElement;

    readonly destinationUniqueIdValue!: string;
    readonly destinationLanguageValue!: string;
    readonly boundingBoxValue!: IBoundingBox;
    readonly poisValue!: PointOfInterestMapMarkerModel[];
    readonly toursValue!: TourMapMarkerModel[];
    readonly readonlyValue!: boolean;
    readonly getJobUrlValue!: string;
    readonly poiCreateUrlValue!: string;
    readonly poiViewUrlValue!: string;
    readonly poiEditUrlValue!: string;
    readonly poiDetailUrlValue!: string;
    readonly isNewPoiDetailPageEnabledValue!: boolean;
    readonly tourDetailUrlValue!: string;
    readonly createPoiFromMapUrlValue!: string;

    map!: Map;
    draw!: MapboxDraw;
    overlay!: Overlay;
    createPoiMarker: Marker | undefined;

    connect() {
        this.containerTarget.onkeydown = e => {
            if (e.keyCode === 46 && this.draw.getSelectedPoints().features.length) {
                this.deletePoi();
            }
        };
        this.map = createMap(this.containerTarget);
        this.overlay = createOverlay(this.containerTarget);

        forceMapToRedrawWhenInitiallyHidden(this.containerTarget, this.map, () => this.resetMapBounds());
        
        const drawModes = {
                ...MapboxDraw.modes, //TODO: single polyline only
                static: StaticMode,
            };

        this.draw = new MapboxDraw({
            defaultMode: 'static',// this.readonlyValue ? 'static' : 'simple_select',
            userProperties: true,
            displayControlsDefault: false,
            // @ts-ignore
            modes: drawModes,
            // styling docs: https://github.com/mapbox/mapbox-gl-draw/blob/main/docs/EXAMPLES.md
            styles: getDrawStyles()
        });

        this.map.addControl(this.draw);

        showTriggerZonePopup(this.map);

        this.map.on('load', (e: MapboxEvent) => {
            // Draw only works after the Mapbox GL JS map has loaded, so you must interact with Draw only after your map's load event
            this.initializeMapFeatures();
        });

        this.map.on('click', 'tours', (e) => {
            if (!e.features || !e.features.length) return;
            const description = e.features[0].properties?.description;
            const coordinates = e.lngLat;

            new Popup()
                .setLngLat(coordinates)
                .setHTML(description)
                .addTo(this.map);
        });

        this.map.on('mouseenter', 'tours', () => {
            this.map.getCanvas().style.cursor = 'pointer';
        });

        this.map.on('mouseleave', 'tours', () => {
            this.map.getCanvas().style.cursor = '';
        });      

        if (!this.readonlyValue) {
            this.setupAddingPoisFromMap();
        }
    }

    initializeMapFeatures() {
        addBoundingBox(this.map, this.boundingBoxValue);
        this.addTours();
        this.addPois();
        this.resetMapBounds();
    }

    private addTours() {
        if (this.map.getLayer('tours')) {
            this.map.removeLayer('tours');
        }
        if (this.map.getSource('tours')) {
            this.map.removeSource('tours');
        }

        const toursSource: FeatureCollection = {
            type: 'FeatureCollection',
            features: this.toursValue.map(tour => {
                const tourDetailUrl = this.tourDetailUrlValue
                    .replace('$tourUniqueId', tour.uniqueId)
                    .replace('$language', this.destinationLanguageValue)
                    .replace('$destinationUniqueId', this.destinationUniqueIdValue);

                const imagePart = tour.mainImageUrl ?
                    `<img src="${tour.mainImageUrl}" width="200px" crossorigin="anonymous" />` :
                    '<span class="text-muted"><i class="fas fa-image fa-9x"></i></span>';

                return {
                    type: 'Feature',
                    properties: {
                        color: getTourColor(tour.index),
                        description: `<div class="d-flex flex-column m-2">
                                <div><a href="${tourDetailUrl}">${imagePart}</a></div>
                                <div class="font-weight-bold" style="max-width: 200px">
                                    <a href="${tourDetailUrl}"
                                       class="text-decoration-none text-primary text-reset">
                                        ${tour.name}
                                    </a>
                                </div>
                        </div>`,
                    },
                    geometry: {
                        type: 'LineString',
                        coordinates: tour.route.map(c => ([c.longitude, c.latitude])),
                    }
                };
            })
        };


        this.map.addSource('tours', {
            type: 'geojson',
            data: toursSource,
        });

        this.map.addLayer({
            id: 'tours',
            type: 'line',
            source: 'tours',
            layout: {
                'line-join': 'round',
                'line-cap': 'round'
            },
            paint: {
                'line-color': ['get', 'color'],
                'line-width': 5
            }
        });
    }

    private addPois() {
        this.poisValue.forEach(poi => {
            const el = document.createElement('div');
            el.innerHTML =
                `<span class="fa-stack fa-2x" title="${poi.name}">
                    <i class="fas fa-map-marker-alt fa-stack-2x" style="text-shadow: 0 0 3px #000; color: ${Colors.smgGreen}"></i>
                </span>`;
            const m = new Marker(el, {
                draggable: false,
                anchor: 'bottom'
            });
            m.setLngLat(new mapboxgl.LngLat(poi.coordinates.longitude, poi.coordinates.latitude));

            const poiDetailUrl = this.poiDetailUrlValue
                .replace('$poiUniqueId', poi.uniqueId)
                .replace('$language', this.destinationLanguageValue)
                .replace('$destinationUniqueId', this.destinationUniqueIdValue);

            m.setPopup(new Popup().setHTML(getPoiMarkerPopupHtml(poi, poiDetailUrl)));

            m.addTo(this.map);

            poi.triggerZones.map(tz => buildMapFeatureForTriggerZone(tz, true, `${poi.name} trigger zone`)).forEach(this.draw.add);
        });
    }

    resetMapBounds() {
        this.map.fitBounds(
            new LngLatBounds(
                new LngLat(this.boundingBoxValue.northWest.longitude, this.boundingBoxValue.northWest.latitude),
                new LngLat(this.boundingBoxValue.southEast.longitude, this.boundingBoxValue.southEast.latitude)
            ),
            { animate: false, padding: 50 });
    }

    setPendingChanges(pendingChanges: boolean) {
        this.saveButtonTarget.disabled = !pendingChanges;
        this.cancelButtonTarget.disabled = !pendingChanges;
    }

    save() {
    }

    cancel() {
        this.draw.deleteAll();
        this.initializeMapFeatures();
    }

    zoomIn() {
        this.map.zoomIn();
    }

    zoomOut() {
        this.map.zoomOut();
    }

    createPoi() {
        if (this.createPoiMarker) return;

        const m = createPoiCreationMarker(this.map, coordinates => 
            this.poiCreateUrlValue
                .replace('$language', this.destinationLanguageValue)
                .replace('$destinationUniqueId', this.destinationUniqueIdValue)
                .replace('$latitude', coordinates.lat.toString())
                .replace('$longitude', coordinates.lng.toString()),
            () => {
                this.createPoiMarker = undefined;
            }
        );

        this.createPoiMarker = m;

        this.dispatch(DomEvents.destinationDetailMapNewPoiCreated);
    }

    deletePoi() {
        this.draw.trash();
    }

    setupAddingPoisFromMap() {
        this.map.on("click", "poi-label", e => {
            if (!e.features || e.features.length === 0) return;

            //console.log(e.features);
            
            const poi = e.features[0];
            if (!poi.id) return;

            const poiName = poi.properties 
                ? poi.properties.name_en ?? poi.properties.name ?? poi.properties.type 
                : "Unknown Place";

            const openStreetMapId = getOsmId(poi.id);
            if (!openStreetMapId) {
                console.log(`Cannot generate Sygic style open street map id, mapbox poi id: ${poi.id}`);
            }

            const createPoiFromMapUrl = this.createPoiFromMapUrlValue
                .replace('$language', this.destinationLanguageValue)
                .replace('$destinationUniqueId', this.destinationUniqueIdValue)
                .replace('$latitude', e.lngLat.lat.toString())
                .replace('$longitude', e.lngLat.lng.toString())
                .replace('$name', encodeURIComponent(poiName))
                .replace('$openStreetMapId', openStreetMapId ?? "")
                ;

            new Popup()
                .setLngLat(e.lngLat)
                .setHTML(
                    `<div class="d-flex flex-column m-2">
                        <label>${(poiName)}</label>
                        <div class="font-weight-bold" style="max-width: 200px">
                            <a href="${createPoiFromMapUrl}"
                                class="form-control btn btn-sm btn-outline-primary">
                                <i class="fas fa-plus-circle"></i> Create new Place
                            </a>
                        </div>
                    </div>`)
                .addTo(this.map);
        });

        this.map.on('mouseenter', "poi-label", () => {
            this.map.getCanvas().style.cursor = "pointer";
        });
        this.map.on('mouseleave', "poi-label", () => {
            this.map.getCanvas().style.cursor = "";
        });
    }
}
